import { Subscription, observable } from '../Signal';
import { empty, hasExpression, log, debug } from './Compiler';
import { Expression, snakeToCamel } from './expressions/Expression';
import { ViewExpression } from './expressions/ViewExpression';
import { ViewRegistry } from './View';
import { translate } from '../Lang';
import { data, route, asset, lookup } from '../Setup';
import { isPropertyObserver } from '../Observer';
import { RenderExpression, TextExpression, CSSExpression, AttributeExpression } from './expressions/RenderExpression';
import { NodeEventExpression } from './expressions/ClosureExpression';


export interface TemplateContext {
	registry:ViewRegistry,
	data: {},
	[x: string]: any
}


export class Template {

	public node:Element;
	public context:TemplateContext;
	public expressions:Array<Expression> = [];
	public content:string|Element;

	private dataSubscription:Subscription;

	constructor( content:string|Element ) {
		this.content = content;
		this.context = {
			data: {},
			registry: new ViewRegistry(),
		}

		Object.setPrototypeOf( this.context, context );
	}

	render() {

		this.node = ( this.content instanceof Element ) ? this.content : createNode( this.content );

		// inject template reference
		this.node['template'] = this;

		// recusive search for expression
		this.traverse();

		// initial render
		this.expressions.forEach( expr => {
			if( expr instanceof RenderExpression ) expr.inital();
		})
	}

	traverse() {

		// handle children
		let treeWalker = document.createTreeWalker( this.node, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
			// do not process scripts
			acceptNode: ( n:Node ) => {
				return n.nodeName.toLowerCase() == 'script' ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
			}
		} );

		let current:Node = this.node;

		while( current ) {



			this.processNode( current as Element|Text );

			// dont dig into view nodes
			if( current instanceof Element && current.getAttribute( 'view' ) ) {
				current = treeWalker.nextSibling();
				// there is no sibling if it is the last child - goto ancestor's sibling
				if( !current ) {
					while( treeWalker.parentNode() ) {
						if( current = treeWalker.nextSibling() ) break;
					}
				}
				continue;
			}

			current = treeWalker.nextNode();
		}
	}

	processNode( node:Element|Text ) {

		// check on text node
		if( node instanceof Text ) {

			// only if there is an expression
			if( hasExpression( node.nodeValue ) ) {
				this.expressions.push( new TextExpression( node, this.context ) );
			}

			return;
		}

		// handle <.. view="..."> nodes
		if( node.getAttribute( 'view' ) ) {
			this.processAttributes( node );

			let expr = new ViewExpression( node, this );
			this.expressions.push( expr );

			return;
		}

		// handle events and attribute expressions
		this.processAttributes( node );
	}



	processAttributes( node:Element ) {

		Array.prototype.slice.call( node.attributes ).forEach( ( attr:Attr ) => {

			// do not create expressions for view= and data-..= attributes
			if( attr.name.indexOf( 'data-' ) == 0 || attr.name == 'view' ) return;

			// handle node events
			if( attr.name.indexOf( 'on-' ) == 0 ) {
				let event = snakeToCamel( attr.name.replace( 'on-', '' ) );

				// check if its a native node event - check on "on.." property like "onclick"
				if( node[ 'on' + event ] !== undefined ) {
					let expr = new NodeEventExpression( this.context, attr.value, event, node );
					this.expressions.push( expr );
					node.removeAttribute( attr.name );
				}

			// handle attribute expressions & ignore view data attributes & ignore
			} else if( hasExpression( attr.value ) ) {
				let kind = ( attr.name == "class" ) ? CSSExpression : AttributeExpression;
				let expr =  new kind( node, attr, this.context );
				this.expressions.push( expr );
			}
		});
	}



	destroy() {
		this.expressions.forEach( expr => expr.destroy() );
	}

	/**
	 * Returns an array of expressions with the given filter
	 * @param filter the callback for the filter
	 */
	filterExpressions( filter:( expr:Expression ) => boolean ) {
		return this.expressions.filter( filter );
	}

	/**
	 * Extends this context from the given parent context.
	 * Injecting the parent reference and setup prototype chain
	 * @param parent The parent context
	 */
	extendContext( parent:TemplateContext ) {
		this.context.parent = parent;
		Object.setPrototypeOf( this.context, parent );
	}

}


function createNode( html:string ) {

	let template = document.createElement( 'template' );
	template.innerHTML = html;

	// if template has one root node, return that
	if( template.content.children.length == 1 ) {
		return template.content.children[0];

	// if template has multiple root nodes wrap them into a div
	} else {
		let temp = document.createElement('div' );
		temp.appendChild( template.content );
		return temp;
	}
}





/**
 * The global template context that is available as "this..." in all template expressions
 */
export var context:any = {
	logging: false,
	empty: empty,
	log: ( ...params ) => {
		if( !context.logging ) return;
		log( ...params );
	},
	// inject translations
	trans: translate,
	// inject setup helpers
	setup: {
		data:data,
		route:route,
		asset:asset,
		lookup:lookup
	},
	// inject debugger
	debug: () => {
		debugger;
	}
}


/**
 * A helper function to template scan on a node or a html string.
 * @param opts.root The root node or html string to scan for expressions
 * @param opts.registry The view registry to lookup views in view=".." expressions
 * @param opts.data The data passed into the template.
 */
export function scan( opts:{ root:Element|string, registry?:ViewRegistry, data?:any } ) {
	let template = new Template( opts.root );

	// setup registry
	if( opts.registry ) template.context.registry = opts.registry;

	// setup data
	if( opts.data ) template.context.data = opts.data;

	template.render();

	return template;
}







